import React, { useEffect, useRef } from 'react';
import { DOUTO_BANNER_AVISO, DOUTO_BANNER_PLACEMENTS, bannerUrl, captureBanner } from '../lib/doutoBanner';
import { doutoBannersEnabled } from '../lib/flags';
import { useLocale } from './LocaleProvider';

const SPANISH_COPY = {
  'resultado-lateral': ['¿Y después del resultado? Consulta dudas de conducta con Douto. Primer caso gratis + 7 días de garantía.', 'Conocer Douto'],
  'resultado-rodape': ['¿Y después del resultado? Consulta dudas de conducta con Douto. Primer caso gratis + 7 días de garantía.', 'Conocer Douto'],
  'copiou-prontuario': ['Copiado. ¿Necesitas apoyo para decidir la conducta?', 'Conoce Douto'],
  'guia-fim': ['¿Tienes dudas sobre la conducta después del puntaje? Consulta Douto. Primer caso gratis + 7 días de garantía.', 'Conocer Douto'],
  'calculadora-margem': ['¿Tienes dudas después del puntaje? Douto ofrece apoyo para la decisión clínica. Primer caso gratis + 7 días de garantía.', 'Conocer Douto'],
  menu: ['', 'Conoce Douto'],
  'home-faixa': ['Apoyo para la decisión clínica: consulta tus dudas sobre la conducta con Douto. Primer caso gratis + 7 días de garantía.', 'Conocer Douto'],
};

const link = 'font-semibold text-[var(--accent)] underline underline-offset-2 hover:text-[var(--accent-hover)]';

/**
 * Banner do Douto. Sempre um <aside> fora do conteúdo principal, sem animação, sem pop-up,
 * escondido na impressão e sem tomar o foco. O evento de visualização conta uma vez por montagem,
 * com 50% visível por 1 s. Variantes:
 *  - "lateral": coluna estreita ao lado do modal ou da calculadora (posição vem de `className`)
 *  - "linha": uma linha discreta
 *  - "faixa": faixa sóbria em largura de conteúdo
 *  - "mensagem": texto curto ao lado do botão de copiar (sem aside próprio, para não ficar solto)
 *  - "menu": link simples do cabeçalho
 */
export default function DoutoBanner({ placement, variant = 'faixa', className = '', onlyWhenEnabled = true }) {
  const { locale } = useLocale();
  const config = DOUTO_BANNER_PLACEMENTS[placement];
  const [texto, cta] = locale === 'es' && SPANISH_COPY[placement] ? SPANISH_COPY[placement] : [config?.texto, config?.cta];
  const ref = useRef(null);
  const enabled = !onlyWhenEnabled || doutoBannersEnabled();

  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el || typeof IntersectionObserver === 'undefined') return undefined;
    let timer;
    let sent = false;
    const observer = new IntersectionObserver(([entry]) => {
      // um banner da página atrás do modal de resultado não foi visto
      const atrasDoModal = document.documentElement.dataset.resultOpen && !el.closest('[role=dialog]');
      if (entry.isIntersecting && entry.intersectionRatio >= 0.5 && !atrasDoModal) {
        if (!timer && !sent) timer = window.setTimeout(() => { sent = true; captureBanner('douto_banner_viewed', placement); }, 1000);
      } else {
        window.clearTimeout(timer);
        timer = undefined;
      }
    }, { threshold: [0, 0.5, 1] });
    observer.observe(el);
    return () => { observer.disconnect(); window.clearTimeout(timer); };
  }, [enabled, placement]);

  if (!enabled || !config) return null;

  const anchor = (
    <a
      href={bannerUrl(placement)}
      target="_blank"
      rel="noopener"
      data-douto-link={placement}
      onClick={() => captureBanner('douto_banner_clicked', placement)}
      className={link}
    >
      {cta}<span className="sr-only">{locale === 'es' ? ' (abre en una pestaña nueva)' : ' (abre em nova aba)'}</span>
    </a>
  );

  if (variant === 'menu') {
    return (
      <a
        ref={ref}
        href={bannerUrl(placement)}
        target="_blank"
        rel="noopener"
        data-douto-link={placement}
        onClick={() => captureBanner('douto_banner_clicked', placement)}
        className={className}
      >
        {cta}<span className="sr-only">{locale === 'es' ? ' (abre en una pestaña nueva)' : ' (abre em nova aba)'}</span>
      </a>
    );
  }

  if (variant === 'mensagem') {
    return (
      <span ref={ref} data-douto-banner={placement} className={`no-print inline-block text-[length:var(--fs-14)] text-[var(--ink-2)] ${className}`}>
        {texto} {anchor}
      </span>
    );
  }

  const box = variant === 'lateral'
    ? 'rounded-[8px] border border-[var(--line)] bg-[var(--hover)] p-3 text-[length:var(--fs-14)]'
    : variant === 'linha'
      ? 'text-[length:var(--fs-14)]'
      : 'rounded-[8px] border border-[var(--line)] bg-[var(--hover)] px-4 py-3 text-[length:var(--fs-15)]';

  return (
    <aside ref={ref} aria-label={locale === 'es' ? 'Douto, apoyo a la decisión clínica' : 'Douto, apoio à decisão clínica'} data-douto-banner={placement} data-nosnippet className={`no-print text-[var(--ink-2)] ${box} ${className}`}>
      <p className="leading-snug">{texto}</p>
      <p className="mt-1.5">{anchor}</p>
      {variant !== 'linha' && <p className="mt-1.5 text-[length:var(--fs-13)] leading-snug">{locale === 'es' ? 'Para profesionales de salud. Apoyo a la decisión clínica; no sustituye el juicio profesional.' : DOUTO_BANNER_AVISO}</p>}
    </aside>
  );
}
